import { useState } from "react";
import { Helmet } from "react-helmet";
import { Tag, Button, Input, Select, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import BreadCrumb from "../Header/SecondHeader";
import AccessDenied from "../AccessDenied";
import usePermission from "../../hooks/usePermission";
import { formatDateString } from "../../JsHelpers/DateFormat";
import { useGetLocationRequestsQuery } from "../../Redux/service/LocationRequests/LocationRequests";
import ReviewLocationRequestModal from "./ReviewLocationRequestModal";
import LocationPreviewMap from "./LocationPreviewMap";

const STATUS_COLORS = {
  pending: "orange",
  confirmed: "green",
  rejected: "red",
};

const STATUS_OPTIONS = [
  { label: trans("Pending"), value: "pending" },
  { label: trans("Confirmed"), value: "confirmed" },
  { label: trans("Rejected"), value: "rejected" },
];

const LocationRequests = () => {
  const { view, edit: canEdit } = usePermission("LOCATION_REQUESTS");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [sort, setSort] = useState({ sortBy: "createdAt", sortOrder: "desc" });
  const [reviewTarget, setReviewTarget] = useState(null); // { request, decision }
  const [mapRecord, setMapRecord] = useState(null);

  const { data, isFetching, refetch } = useGetLocationRequestsQuery(
    {
      page: currentPage,
      limit,
      search,
      status: statusFilter.length ? statusFilter.join(",") : undefined,
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    },
    { refetchOnMountOrArgChange: true },
  );

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (sorter?.order) {
      setSort({
        sortBy: sorter.field,
        sortOrder: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({ sortBy: "createdAt", sortOrder: "desc" });
    }
  };

  const columns = [
    {
      title: trans("Task"),
      key: "task",
      width: 160,
      render: (_, record) => record.task?.code || record.task?.name || "-",
    },
    {
      title: trans("POI"),
      key: "poi",
      width: 180,
      render: (_, record) => record.poi?.name || "-",
    },
    {
      title: trans("Submitted By"),
      key: "submittedBy",
      width: 160,
      render: (_, record) => record.submittedBy?.name || "-",
    },
    {
      title: trans("Previous Coordinates"),
      key: "previous",
      width: 160,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setMapRecord(record)}
        >
          {trans("View Map")}
        </Button>
      ),
    },
    {
      title: trans("Proposed Coordinates"),
      key: "proposed",
      width: 160,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setMapRecord(record)}
        >
          {trans("View Map")}
        </Button>
      ),
    },
    {
      title: trans("Note"),
      dataIndex: "note",
      key: "note",
      width: 200,
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"} className="text-capitalize">
          {trans(status)}
        </Tag>
      ),
    },
    {
      title: trans("Reviewed By"),
      key: "reviewedBy",
      width: 150,
      render: (_, record) => record.reviewedBy?.name || "-",
    },
    {
      title: trans("Created At"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      sorter: true,
      render: (v) => formatDateString(v),
    },
    {
      title: trans("Actions"),
      key: "actions",
      width: 180,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) =>
        record.status === "pending" && canEdit ? (
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => setReviewTarget({ request: record, decision: "confirmed" })}
            >
              {trans("Confirm")}
            </Button>
            <Button
              danger
              size="small"
              onClick={() => setReviewTarget({ request: record, decision: "rejected" })}
            >
              {trans("Reject")}
            </Button>
          </Space>
        ) : (
          "-"
        ),
    },
  ];

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>{trans("Location Requests")}</title>
        <meta name="description" content="Location Requests" />
      </Helmet>
      <BreadCrumb
        secButton={trans("Refresh")}
        secClick={() => refetch()}
        secLoading={isFetching}
      />
      <div className="bg-white px-3 py-2 d-flex gap-2 flex-wrap align-items-center">
        <Input
          prefix={<SearchOutlined style={{ color: "#aaa" }} />}
          placeholder={trans("Search by POI, technician or task code")}
          allowClear
          style={{ maxWidth: 320 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder={trans("Status")}
          style={{ minWidth: 220 }}
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="bg-white-rounded-top mt-3 table-parent">
        <AntdTable
          columns={columns}
          loading={isFetching}
          dataSource={data?.data || []}
          pageSize={limit}
          totalItems={data?.pagination?.totalItems || 0}
          currentPage={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setCurrentPage(1);
          }}
          onTableChange={handleTableChange}
          scrollX={true}
        />
      </div>

      <ReviewLocationRequestModal
        request={reviewTarget?.request}
        decision={reviewTarget?.decision}
        onClose={() => {
          setReviewTarget(null);
          refetch();
        }}
      />

      <LocationPreviewMap
        open={!!mapRecord}
        record={mapRecord}
        onClose={() => setMapRecord(null)}
      />
    </>
  );
};

export default LocationRequests;
